import React, { useState, useEffect, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { TextField, Button, Box, Typography, Container, Autocomplete, CircularProgress, Modal, List, ListItemButton, ListItemText, Divider } from '@mui/material';
import { debounce } from 'lodash';
import PessoaForm from './PessoaForm';
import VeiculoForm from './VeiculoForm';
import { formatUtcForDateTimeLocal } from '../utils/dateTime';
import FormCard from './FormCard';
import { modalSurfaceSx } from '../styles/uiTokens';

function AcessoForm({ onAcessoAdded, initialData, onCancelEdit }) {
  const { user } = useAuth();
  const [id, setId] = useState(null);
  const [empresa_id, setEmpresaId] = useState('');
  const [imovel_id, setImovelId] = useState('');
  const [pessoa_id, setPessoaId] = useState('');
  const [veiculo_id, setVeiculoId] = useState('');
  const [autorizador_id, setAutorizadorId] = useState('');
  const [visitante_id, setVisitanteId] = useState('');
  const [funcionario_entrada_id, setFuncionarioEntradaId] = useState('');
  const [funcionario_saida_id, setFuncionarioSaidaId] = useState('');
  const [entrada, setEntrada] = useState('');
  const [saida, setSaida] = useState('');
  const [observacoes, setObservacoes] = useState('');

  // Autocomplete options states
  const [empresaOptions, setEmpresaOptions] = useState([]);
  const [imovelOptions, setImovelOptions] = useState([]);
  const [veiculoOptions, setVeiculoOptions] = useState([]);
  const [visitanteOptions, setVisitanteOptions] = useState([]);
  // Separate states for each person-related field
  const [pessoaOptions, setPessoaOptions] = useState([]);
  const [autorizadorOptions, setAutorizadorOptions] = useState([]);
  const [funcEntradaOptions, setFuncEntradaOptions] = useState([]);
  const [funcSaidaOptions, setFuncSaidaOptions] = useState([]);

  // States for selected objects (for Autocomplete value prop)
  const [selectedEmpresa, setSelectedEmpresa] = useState(null);
  const [selectedImovel, setSelectedImovel] = useState(null);
  const [selectedPessoa, setSelectedPessoa] = useState(null);
  const [selectedVeiculo, setSelectedVeiculo] = useState(null);
  const [selectedAutorizador, setSelectedAutorizador] = useState(null);
  const [selectedVisitante, setSelectedVisitante] = useState(null);
  const [selectedFuncionarioEntrada, setSelectedFuncionarioEntrada] = useState(null);
  const [selectedFuncionarioSaida, setSelectedFuncionarioSaida] = useState(null);
  const [empresaInputValue, setEmpresaInputValue] = useState('');
  const [pessoaInputValue, setPessoaInputValue] = useState('');

  // States for modal visibility
  const [openPessoaModal, setOpenPessoaModal] = useState(false);
  const [openVeiculoModal, setOpenVeiculoModal] = useState(false);
  const [openMoradoresModal, setOpenMoradoresModal] = useState(false);
  const [moradoresList, setMoradoresList] = useState([]);
  const [loadingMoradores, setLoadingMoradores] = useState(false);

  // Loading states for Autocompletes
  const [loadingEmpresas, setLoadingEmpresas] = useState(false);
  const [loadingImoveis, setLoadingImoveis] = useState(false);
  const [loadingPessoas, setLoadingPessoas] = useState(false);
  const [loadingVeiculos, setLoadingVeiculos] = useState(false);
  const [loadingVisitantes, setLoadingVisitantes] = useState(false);

  // Helper functions to open/close modals
  const handleOpenPessoaModal = () => setOpenPessoaModal(true);
  const handleClosePessoaModal = () => setOpenPessoaModal(false);
  const handleOpenVeiculoModal = () => setOpenVeiculoModal(true);
  const handleCloseVeiculoModal = () => setOpenVeiculoModal(false);

  const handleOpenMoradoresModal = () => {
    if (!imovel_id) return;
    setLoadingMoradores(true);
    setOpenMoradoresModal(true);
    api.get(`/moradores?imovelId=${imovel_id}&limit=100`)
      .then(response => setMoradoresList(response.data.data || response.data))
      .catch(error => console.error('Erro ao buscar moradores:', error))
      .finally(() => setLoadingMoradores(false));
  };
  const handleCloseMoradoresModal = () => setOpenMoradoresModal(false);

  const handleSelectMorador = (morador) => {
    const pessoa = { id: morador.pessoa_id, nome: morador.pessoa_nome };
    setSelectedAutorizador(pessoa);
    setAutorizadorId(pessoa.id);
    setAutorizadorOptions([pessoa]);
    setOpenMoradoresModal(false);
  };

  const modalContainerSx = {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    minHeight: '100dvh',
    py: { xs: 1, sm: 2 },
    px: { xs: 1, sm: 2 },
    overflowY: 'auto',
  };

  const modalContentSx = {
    width: { xs: '100%', sm: 520 },
    maxWidth: '100%',
    mt: { xs: 0, sm: 2 },
    maxHeight: 'calc(100dvh - 32px)',
    overflowY: 'auto',
    p: { xs: 2, sm: 4 },
    ...modalSurfaceSx,
  };

  const debouncedFetchEmpresas = useMemo(() => 
    debounce((searchTerm) => {
      setLoadingEmpresas(true);
      api.get(`/empresas?search=${searchTerm}`)
        .then(response => setEmpresaOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar empresas:', error))
        .finally(() => setLoadingEmpresas(false));
    }, 500),
    []
  );
  const debouncedFetchImoveis = useMemo(() => 
    debounce((searchTerm) => {
      setLoadingImoveis(true);
      api.get(`/imoveis?search=${encodeURIComponent(searchTerm)}&page=0&limit=20`)
        .then(response => setImovelOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar imoveis:', error))
        .finally(() => setLoadingImoveis(false));
    }, 500),
    []
  );

  // Generic debounced fetch for people, accepting a state setter
  const debouncedFetchPessoas = useMemo(() => 
    debounce((searchTerm, setter) => {
      setLoadingPessoas(true);
      api.get(`/pessoas?search=${encodeURIComponent(searchTerm)}`)
        .then(response => setter(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar pessoas:', error))
        .finally(() => setLoadingPessoas(false));
    }, 500),
    []
  );

  const debouncedFetchVeiculos = useMemo(() => 
    debounce((searchTerm) => {
      setLoadingVeiculos(true);
      api.get(`/veiculos?search=${searchTerm}`)
        .then(response => setVeiculoOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar veiculos:', error))
        .finally(() => setLoadingVeiculos(false));
    }, 500),
    []
  );

  const debouncedFetchVisitantes = useMemo(() => 
    debounce((searchTerm) => {
      setLoadingVisitantes(true);
      api.get(`/visitantes?search=${searchTerm}`)
        .then(response => setVisitanteOptions(response.data.data || response.data))
        .catch(error => console.error('Erro ao buscar visitantes:', error))
        .finally(() => setLoadingVisitantes(false));
    }, 500),
    []
  );

  useEffect(() => {
    return () => {
      debouncedFetchEmpresas.cancel();
      debouncedFetchImoveis.cancel();
      debouncedFetchPessoas.cancel();
      debouncedFetchVeiculos.cancel();
      debouncedFetchVisitantes.cancel();
    };
  }, [
    debouncedFetchEmpresas,
    debouncedFetchImoveis,
    debouncedFetchPessoas,
    debouncedFetchVeiculos,
    debouncedFetchVisitantes,
  ]);

  useEffect(() => {
    if (initialData) {
      setId(initialData.id);
      setEmpresaId(initialData.empresa_id);
      setImovelId(initialData.imovel_id);
      setPessoaId(initialData.pessoa_id || '');
      setVeiculoId(initialData.veiculo_id || '');
      setAutorizadorId(initialData.autorizador_id || '');
      setVisitanteId(initialData.visitante_id || '');
      setFuncionarioEntradaId(initialData.funcionario_entrada_id || '');
      setFuncionarioSaidaId(initialData.funcionario_saida_id || '');
      //console.log(`data recebida ${initialData.entrada}`)
      setEntrada(formatUtcForDateTimeLocal(initialData.entrada, user));
      setSaida(formatUtcForDateTimeLocal(initialData.saida, user));
      setObservacoes(initialData.observacoes || '');

      // Helper function to fetch and set selected object and options
      const fetchAndSetSelected = async (id, apiPath, setSelectedState, setOptionsState) => {
        //console.log(`[fetchAndSetSelected] Chamado para apiPath: ${apiPath}, ID: ${id}`);
        if (id) {
          try {
            const response = await api.get(`${apiPath}?id=${id}`);
            //console.log(`[fetchAndSetSelected] Resposta da API para ${apiPath}?id=${id}:`, response.data);
            let item = null;
            // Robustly extract item from response, handling array or direct object
            if (Array.isArray(response.data.data)) {
                item = response.data.data[0];
            } else if (response.data.data) { // if response.data.data is an object
                item = response.data.data;
            } else if (Array.isArray(response.data)) {
                item = response.data[0];
            } else if (response.data) { // if response.data is an object
                item = response.data;
            }
            
            if (item) {
              //console.log(`[fetchAndSetSelected] Item extraido para ${apiPath}:`, item);
              setSelectedState(item);
              setOptionsState([item]); // Ensure the selected item is in options
            } else {
              //console.log(`[fetchAndSetSelected] Nenhum item encontrado para ${apiPath}?id=${id}. Limpando estados.`);
              setSelectedState(null);
              setOptionsState([]);
            }
          } catch (error) {
            console.error(`[fetchAndSetSelected] Erro ao buscar item ${id} de ${apiPath}:`, error);
            setSelectedState(null); // Clear on error
            setOptionsState([]); // Clear on error
          }
        } else {
          //console.log(`[fetchAndSetSelected] ID vazio para ${apiPath}. Limpando estados.`);
          setSelectedState(null);
          setOptionsState([]);
        }
      };

      fetchAndSetSelected(initialData.empresa_id, '/empresas', setSelectedEmpresa, setEmpresaOptions);
      fetchAndSetSelected(initialData.imovel_id, '/imoveis', setSelectedImovel, setImovelOptions);
      fetchAndSetSelected(initialData.pessoa_id, '/pessoas', setSelectedPessoa, setPessoaOptions);
      fetchAndSetSelected(initialData.veiculo_id, '/veiculos', setSelectedVeiculo, setVeiculoOptions);
      fetchAndSetSelected(initialData.autorizador_id, '/pessoas', setSelectedAutorizador, setAutorizadorOptions);
      fetchAndSetSelected(initialData.visitante_id, '/visitantes', setSelectedVisitante, setVisitanteOptions);
      fetchAndSetSelected(initialData.funcionario_entrada_id, '/pessoas', setSelectedFuncionarioEntrada, setFuncEntradaOptions);
      fetchAndSetSelected(initialData.funcionario_saida_id, '/pessoas', setSelectedFuncionarioSaida, setFuncSaidaOptions);

    } else {
      clearForm();
      setEntrada('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialData, user]);

  const clearForm = () => {
    setId(null);
    setEmpresaId(user?.empresa_id || '');
    setImovelId('');
    setPessoaId('');
    setVeiculoId('');
    setAutorizadorId('');
    setVisitanteId('');
    setFuncionarioSaidaId('');
    setEntrada('');
    setSaida('');
    setObservacoes('');
    // Clear all autocomplete options
    setEmpresaOptions([]);
    setImovelOptions([]);
    setVeiculoOptions([]);
    setVisitanteOptions([]);
    setPessoaOptions([]);
    setAutorizadorOptions([]);
    setFuncSaidaOptions([]);
    // Clear all selected objects
    setSelectedEmpresa(null);
    setEmpresaInputValue('');
    setSelectedImovel(null);
    setSelectedPessoa(null);
    setPessoaInputValue('');
    setSelectedVeiculo(null);
    setSelectedAutorizador(null);
    setSelectedVisitante(null);
    setSelectedFuncionarioSaida(null);

    // Pre-fill funcionario_entrada com a pessoa vinculada ao usuário logado
    if (user?.pessoa_id) {
      setFuncionarioEntradaId(user.pessoa_id);
      api.get(`/pessoas?id=${user.pessoa_id}`)
        .then(response => {
          const items = response.data.data || response.data;
          const pessoa = Array.isArray(items) ? items[0] : items;
          if (pessoa) {
            setSelectedFuncionarioEntrada(pessoa);
            setFuncEntradaOptions([pessoa]);
          }
        })
        .catch(err => console.error('Erro ao buscar pessoa do usuário:', err));
    } else {
      setFuncionarioEntradaId('');
      setSelectedFuncionarioEntrada(null);
      setFuncEntradaOptions([]);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const acessoData = {
      empresa_id: user.tipo_usuario === 'admin' ? empresa_id : user.empresa_id,
      imovel_id,
      pessoa_id: pessoa_id || null,
      veiculo_id: veiculo_id || null,
      autorizador_id: autorizador_id || null,
      visitante_id: visitante_id || null,
      funcionario_entrada_id: funcionario_entrada_id || null,
      funcionario_saida_id: funcionario_saida_id || null,
      observacoes: observacoes || null,
    };

    if (id) { // Editing existing record
      acessoData.entrada = entrada || null;
      acessoData.saida = saida || null;
    } else { // Creating new record
      // 'entrada' will be set by the backend (NOW()).
      // 'saida' will be null by default.
    }

    const apiCall = id ? api.put(`/acessos/${id}`, acessoData) : api.post('/acessos', acessoData);

    apiCall
      .then(() => {
        clearForm();
        onAcessoAdded();
        if (id) onCancelEdit();
      })
      .catch(error => {
        console.error('Erro ao salvar acesso:', error);
      });
  };

  return (
    <Container maxWidth="lg">
      <FormCard component="form" onSubmit={handleSubmit} sx={{ width: '100%', maxWidth: 1280 }}>
        <Typography variant="h5" gutterBottom>
          {id ? 'Editar Acesso' : 'Adicionar Novo Acesso'}
        </Typography>

        {user?.tipo_usuario === 'admin' && (
          <Autocomplete
            id="empresa-autocomplete"
            options={empresaOptions}
            getOptionLabel={(option) => option.nome || ''}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            value={selectedEmpresa}
             onChange={(event, newValue) => {
               setSelectedEmpresa(newValue);
               setEmpresaId(newValue ? newValue.id : '');
               setEmpresaInputValue(newValue?.nome || '');
             }}
             inputValue={empresaInputValue}
             onInputChange={(event, newInputValue, reason) => {
               if (reason === 'input' || reason === 'clear') {
                 setEmpresaInputValue(newInputValue);
                 if (newInputValue) {
                  debouncedFetchEmpresas(newInputValue);
                } else {
                  setEmpresaOptions([]);
                }
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Empresa"
                variant="outlined"
                margin="normal"
                required
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingEmpresas ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            disabled={!!initialData}
          />
        )}

        <Autocomplete
          id="imovel-autocomplete"
          options={imovelOptions}
          getOptionLabel={(option) => `${option.descricao} - ${option.numero}` || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedImovel}
            onChange={(event, newValue) => {
              setSelectedImovel(newValue);
              setImovelId(newValue ? newValue.id : '');
            }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchImoveis(newInputValue);
              } else {
                setImovelOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Imovel"
              variant="outlined"
              margin="normal"
              required
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingImoveis ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
          disabled={!!initialData}
        />

        <Autocomplete
          id="pessoa-autocomplete"
          options={pessoaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedPessoa}
           onChange={(event, newValue) => {
             setSelectedPessoa(newValue);
             setPessoaId(newValue ? newValue.id : '');
             setPessoaInputValue(newValue?.nome || '');
           }}
           inputValue={pessoaInputValue}
           onInputChange={(event, newInputValue, reason) => {
             if (reason === 'input' || reason === 'clear') {
               setPessoaInputValue(newInputValue);
               if (newInputValue) {
                debouncedFetchPessoas(newInputValue, setPessoaOptions);
              } else {
                setPessoaOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Pessoa"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    <Button onClick={handleOpenPessoaModal} size="small" sx={{ ml: 1 }}>
                      Cadastrar
                    </Button>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="veiculo-autocomplete"
          options={veiculoOptions}
          getOptionLabel={(option) => `${option.placa} (${option.marca_nome || option.marca} ${option.modelo})` || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedVeiculo}
          onChange={(event, newValue) => {
            setSelectedVeiculo(newValue);
            setVeiculoId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchVeiculos(newInputValue);
              } else {
                setVeiculoOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Veiculo"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingVeiculos ? <CircularProgress color="inherit" size={20} /> : null}
                    <Button onClick={handleOpenVeiculoModal} size="small" sx={{ ml: 1 }}>
                      Cadastrar
                    </Button>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="autorizador-autocomplete"
          options={autorizadorOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedAutorizador}
          onChange={(event, newValue) => {
            setSelectedAutorizador(newValue);
            setAutorizadorId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchPessoas(newInputValue, setAutorizadorOptions);
              } else {
                setAutorizadorOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Autorizador (Pessoa)"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    <Button onClick={handleOpenMoradoresModal} size="small" sx={{ ml: 1 }} disabled={!imovel_id}>
                      Moradores
                    </Button>
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="visitante-autocomplete"
          options={visitanteOptions}
          getOptionLabel={(option) => `${option.id} - ${option.data_agendamento} ${option.hora_agendamento}` || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedVisitante}
          onChange={(event, newValue) => {
            setSelectedVisitante(newValue);
            setVisitanteId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchVisitantes(newInputValue);
              } else {
                setVisitanteOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Visitante (Agendamento)"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingVisitantes ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="funcionario-entrada-autocomplete"
          options={funcEntradaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedFuncionarioEntrada}
          onChange={(event, newValue) => {
            setSelectedFuncionarioEntrada(newValue);
            setFuncionarioEntradaId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchPessoas(newInputValue, setFuncEntradaOptions);
              } else {
                setFuncEntradaOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Funcionario Entrada"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <Autocomplete
          id="funcionario-saida-autocomplete"
          options={funcSaidaOptions}
          getOptionLabel={(option) => option.nome || ''}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          value={selectedFuncionarioSaida}
          onChange={(event, newValue) => {
            setSelectedFuncionarioSaida(newValue);
            setFuncionarioSaidaId(newValue ? newValue.id : '');
          }}
          onInputChange={(event, newInputValue, reason) => {
            if (reason === 'input') {
              if (newInputValue) {
                debouncedFetchPessoas(newInputValue, setFuncSaidaOptions);
              } else {
                setFuncSaidaOptions([]);
              }
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Funcionario Saida"
              variant="outlined"
              margin="normal"
              InputProps={{
                ...params.InputProps,
                endAdornment: (
                  <>
                    {loadingPessoas ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />

        <TextField
          label="Entrada"
          variant="outlined"
          fullWidth
          type="datetime-local"
          value={entrada}
          onChange={(e) => setEntrada(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          disabled={!id}
          helperText={id ? `Exibido no fuso da empresa (${user?.empresa_timezone || 'America/Sao_Paulo'}).` : 'Na criação, a entrada é definida pelo servidor.'}
        />
        <TextField
          label="Saida"
          variant="outlined"
          fullWidth
          type="datetime-local"
          value={saida}
          onChange={(e) => setSaida(e.target.value)}
          margin="normal"
          InputLabelProps={{ shrink: true }}
          disabled={!id}
          helperText={id ? `Exibido no fuso da empresa (${user?.empresa_timezone || 'America/Sao_Paulo'}).` : 'A saída é definida na edição.'}
        />
        <TextField
          label="Observacoes"
          variant="outlined"
          fullWidth
          multiline
          rows={3}
          value={observacoes}
          onChange={(e) => setObservacoes(e.target.value)}
          margin="normal"
        />
        <Button
          type="submit"
          variant="contained"
          color="primary"
          sx={{ mt: 2, mr: 2 }}
        >
          {id ? 'Atualizar Acesso' : 'Adicionar Acesso'}
        </Button>
        <Button
          type="button"
          variant="outlined"
          color="secondary"
          sx={{ mt: 2 }}
          onClick={onCancelEdit}
        >
          Cancelar
        </Button>
      </FormCard>

      {/* Pessoa Modal */}
      <Modal
        open={openPessoaModal}
        onClose={handleClosePessoaModal}
        aria-labelledby="modal-title-pessoa"
        aria-describedby="modal-description-pessoa"
      >
        <Box sx={modalContainerSx}>
          <Box sx={modalContentSx}>
            <Typography id="modal-title-pessoa" variant="h6" component="h2">
              Cadastrar Nova Pessoa
            </Typography>
            <PessoaForm
              onPessoaAdded={(newPessoa) => {
                handleClosePessoaModal();
                setSelectedPessoa(newPessoa);
                setPessoaId(newPessoa.id);
                setPessoaOptions(prevOptions => {
                  // Add newPessoa to the options list if not already present
                  if (!prevOptions.some(p => p.id === newPessoa.id)) {
                    return [...prevOptions, newPessoa];
                  }
                  return prevOptions;
                });
              }}
              onCancelEdit={handleClosePessoaModal} // Use onCancelEdit to close modal
              initialData={null} // Ensure it's a new form
            />
          </Box>
        </Box>
      </Modal>

      {/* Veiculo Modal */}
      <Modal
        open={openVeiculoModal}
        onClose={handleCloseVeiculoModal}
        aria-labelledby="modal-title-veiculo"
        aria-describedby="modal-description-veiculo"
      >
        <Box sx={modalContainerSx}>
          <Box sx={modalContentSx}>
            <Typography id="modal-title-veiculo" variant="h6" component="h2">
              Cadastrar Novo Veiculo
            </Typography>
            <VeiculoForm
              onVeiculoAdded={(newVeiculo) => {
                handleCloseVeiculoModal();
                setSelectedVeiculo(newVeiculo);
                setVeiculoId(newVeiculo.id);
                setVeiculoOptions(prevOptions => {
                  if (!prevOptions.some(v => v.id === newVeiculo.id)) {
                    return [...prevOptions, newVeiculo];
                  }
                  return prevOptions;
                });
              }}
              onCancelEdit={handleCloseVeiculoModal}
              initialData={null}
            />
          </Box>
        </Box>
      </Modal>

      {/* Moradores Modal */}
      <Modal
        open={openMoradoresModal}
        onClose={handleCloseMoradoresModal}
        aria-labelledby="modal-title-moradores"
      >
        <Box sx={modalContainerSx}>
          <Box sx={modalContentSx}>
            <Typography id="modal-title-moradores" variant="h6" component="h2" sx={{ mb: 2 }}>
              Moradores da Unidade
            </Typography>
            {loadingMoradores ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : moradoresList.length === 0 ? (
              <Typography color="text.secondary">Nenhum morador encontrado nesta unidade.</Typography>
            ) : (
              <List disablePadding>
                {moradoresList.map((morador, index) => (
                  <React.Fragment key={morador.id}>
                    {index > 0 && <Divider />}
                    <ListItemButton onClick={() => handleSelectMorador(morador)}>
                      <ListItemText primary={morador.pessoa_nome} secondary={morador.imovel_descricao ? `${morador.imovel_descricao} - ${morador.imovel_numero}` : null} />
                    </ListItemButton>
                  </React.Fragment>
                ))}
              </List>
            )}
          </Box>
        </Box>
      </Modal>
    </Container>
  );
}

export default AcessoForm;



